import React, {useEffect, useMemo, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import Button from "../../../../../components/button/button.jsx";
import {FaEraser, FaFileExcel, FaPlus, FaPrint, FaSearch} from "react-icons/fa";
import {Card} from "../../../../../components/card/card.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import {useQuery} from "@tanstack/react-query";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import InputCheckbox from "../../../../../components/input/input-checkbox.jsx";
import InputDateTime from "../../../../../components/input/input-datetime.jsx";
import {format} from "date-fns";
import userService from "../../../../../services/web/user-service.js";
import materialService from "../../../../../services/web/material-service.js";
import materialCautionService from "../../../../../services/web/material-caution-service.js";
import projectUtils from "../../../../../utils/project-utils.js";
import {useGlobalKey} from "../../../../../hooks/use-global-key.js";
import InputForm from "../../../../../components/input/input-form.jsx";
import SentryRow from "../../../../../components/template/sentry-row.jsx";
import InputDatetime from "../../../../../components/input/input-datetime.jsx";

const initialFilter = {
    start: "",
    end: "",
    requester: [],
    material: "",
    permanent: false
}

const MaterialCautionsList = () => {
    const navigate = navigateHook();
    const [filter, setFilter] = useState(initialFilter);
    const [gridApi, setGridApi] = useState(null);
    const [isLoading, setIsLoading] = useState(false)
    const [cautions, setCautions] = useState([])

    const columnDefs = useMemo(() => [
        {field: "id", hide: true},
        {field: "date", headerName: "Data/Hora entrega", width: 160, comparator: aggridUtils.agGridCompareDateTime},
        {field: "requester", headerName: "Solicitante", flex: 1, comparator: aggridUtils.lowerUpCaseComparator},
        {field: "storage",  headerName: "Depósito", flex: 1, comparator: aggridUtils.lowerUpCaseComparator},
        {field: "material", headerName: "Material", flex: 1, comparator: aggridUtils.lowerUpCaseComparator},
        {field: "due_date", headerName: "Data de vencimento", flex: 1, comparator: aggridUtils.agGridSimpleDateComparator,
            valueFormatter: ({value}) => {
                if(!value) return "";
                const [y,m,d] = value.split("-");
                return `${d}/${m}/${y}`
            }
        },
        {field: "quantity", headerName: "Quantidade",  width: 100}
    ], []);

    const defaultColDef = useMemo(() => {
        return {
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
            suppressFloatingFilterButton: true,
        };
    }, []);

    const { data: requesters = [], isLoading: isLoadingRequesters } = useQuery({
        queryKey: ['requesters'],
        queryFn: async () => {
            return await userService.optionsActiveUserName();
        },
    });

    const { data: materials = [], isLoading: isLoadingMaterials, status } = useQuery({
        queryKey: ['materials'],
        queryFn: async () => {
            return await materialService.optionsCaution();
        },
    });

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const materialParam = searchParams.get("material");

        if (status === "success") {
            let filter = initialFilter
            if (materialParam) {
                filter = {
                    ...initialFilter,
                    material: materialParam
                }

                setFilter(filter)
            }

            search(filter)
        }
    }, [materials, status]);

    const handleDate = (id, value) => {
        const dateFormated = value ? format(value, "yyyy-MM-dd HH:mm") : null
        setFilter(prev => ({ ...prev, [id]: dateFormated }));
    }

    const search = (customFilter = filter) => {
        setIsLoading(true)

        const formData= {}
        Object.entries(customFilter).forEach(([key, value]) => {
            if ((key === "requester") && value) {
                formData[key] = value.map(item => item?.value).filter(Boolean)
            } else {
                formData[key] = value
            }
        });

        materialCautionService.list(formData)
            .then((data) => {
                setCautions(data)
            })
            .catch((error) => {
                console.error(error);
                let message = 'Erro ao carregar.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => setIsLoading(false))
    }

    const clearFilter = () => {
        setFilter(initialFilter)
        search(initialFilter)
    }

    const exportData = () => {
        const filteredData = [];
        gridApi.forEachNodeAfterFilter(node => {
            filteredData.push(node.data);
        });
        const csvDatas = [];

        const csvMaker = (filteredData) => Object.values(filteredData).join(';');

        const newArray = filteredData.map(caution => (
            {
                "Data/Hora entrega": caution.date,
                "Solicitante": caution.requester,
                "Depósito": caution.storage,
                "Material": caution.material,
                "Quantidade": caution.quantity
            }
        ));

        const headers = Object.keys(newArray[0]);

        csvDatas.push(headers.join(';'));

        newArray.map((item) => csvDatas.push(csvMaker(item)));

        const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvDatas.join('\n')], { type: 'text/csv;charset=utf-8' });

        const url = window.URL.createObjectURL(blob);

        window.open(url);
        window.URL.revokeObjectURL(url);
    }

    const openPrint = () => {
        localStorage.removeItem("filter_material_cautions")
        localStorage.removeItem("search_without_filter")

        let hasAnyFilter = false;
        for (const [key, value] of Object.entries(filter)) {
            let hasFilterValue;

            if (key === "requester" || key === "material") {
                hasFilterValue = value.length > 0;
            } else {
                hasFilterValue = !!value;
            }

            if (hasFilterValue) {
                hasAnyFilter = true;
                break;
            }
        }

        if (hasAnyFilter) {
            localStorage.setItem("filter_material_cautions", JSON.stringify(filter))
        } else {
            localStorage.setItem("search_without_filter", "true")
        }

        const url= `/reports/material_cautions`;
        navigate(url, true)
    }

    useGlobalKey("Enter", () => search());
    useGlobalKey("Escape", clearFilter);

    return (
        <Template isLoading={isLoading || isLoadingRequesters || isLoadingMaterials}>
            <Template.Header>
                <Template.Title>
                    Materiais em cautela
                </Template.Title>

                <div className={"flex gap-2"}>
                    <Button target={"success"} onClick={exportData}>
                        <FaFileExcel/><span className="font-bold">Exportar</span>
                    </Button>
                    <Button title="Imprimir" onClick={openPrint}>
                        <FaPrint/>
                    </Button>
                </div>
            </Template.Header>
            <Template.Body>
                <Card>
                    <Card.Body>
                        <SentryRow>
                            <div className={"col-span-4 flex items-stretch"}>
                                <InputDatetime
                                    id="start"
                                    label="Entrega entre"
                                    selected={filter.start}
                                    onChange={(date) => handleDate("start", date)}
                                    dateFormat="LL"
                                    customIcon
                                    inputClass="!rounded-e-none"
                                    classNameWrapper="w-[47%]"
                                />

                                <div className="border border-[#E5E7EB] px-4 py-2 text-sm leading-none">à</div>

                                <InputDatetime
                                    id="end"
                                    selected={filter.end}
                                    onChange={(date) => handleDate("end", date)}
                                    dateFormat="LL"
                                    inputClass="!rounded-s-none"
                                    classNameWrapper="w-[47%]"
                                />
                            </div>

                            <div className="col-span-3">
                                <SelectComponent
                                    id="requester"
                                    value={filter.requester}
                                    options={requesters ?? []}
                                    label={"Solicitante"}
                                    onChange={(value) => {
                                        setFilter(prev => ({...prev, requester: value}))
                                    }}
                                    isMulti
                                />
                            </div>

                            <div className={"col-span-5 flex gap-4"}>
                                <InputForm
                                    id="material"
                                    name="material"
                                    value={filter.material}
                                    onChange={({target: {value}}) => setFilter(prev => ({...prev, material: value}))}
                                    label="Material"
                                    classNameWrapper={"w-full"}/>

                                <InputCheckbox
                                    id="permanent"
                                    checked={filter.permanent}
                                    label="Permanente"
                                    className={'transform translate-y-1.75!'}
                                    onChange={({target: {checked}}) => setFilter(prevState => ({...prevState, permanent: checked}))}/>

                                <div className="flex justify-start gap-2">
                                    <Button target="primary-outline" onClick={clearFilter} title={"Limpar"}>
                                        <FaEraser/>
                                    </Button>
                                    <Button onClick={() => search()}><FaSearch/></Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>
                <Card className="mt-2">
                    <Card.Body>
                        <div className={"h-[calc(100vh-153px)]"}>
                            <AgGridReact
                                onGridReady={(params) => {
                                    setGridApi(params.api);
                                }}
                                pagination
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                rowData={cautions}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme({noHover: true})}
                                onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
};

export default MaterialCautionsList;